import { api } from './http-client';
import {
  FormConfig,
  FormConfigSummary,
  FormFieldDef,
  FormGroupDef,
  FormType,
  StepTranslations,
  FormConfigSectionOption,
  FormConfigSectionOptions,
  FormPricingResponse,
  FormPricingRow,
} from '../interfaces/form-config.interface';

export const formConfigService = {
  listAll: async (): Promise<FormConfigSummary[]> => {
    const response = await api.request<FormConfigSummary[]>({
      url: '/admin/form-configs',
      method: 'GET',
    });
    return response.data;
  },

  getByType: async (formType: FormType): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/type/${formType}`,
      method: 'GET',
    });
    return response.data;
  },

  updateFields: async (configId: string, fields: FormFieldDef[]): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/${configId}/fields`,
      method: 'PUT',
      data: { fields },
    });
    return response.data;
  },

  patchField: async (
    fieldId: string,
    patch: Partial<FormFieldDef>,
  ): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/fields/${fieldId}`,
      method: 'PUT',
      data: patch,
    });
    return response.data;
  },

  updateFieldOptions: async (
    fieldId: string,
    options: Array<{
      value: string;
      sortOrder?: number;
      translations: Record<string, string>;
    }>,
  ): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/fields/${fieldId}/options`,
      method: 'PUT',
      data: { options },
    });
    return response.data;
  },

  reorderSteps: async (
    configId: string,
    steps: Array<{ stepNumber: number; translations?: StepTranslations }>,
  ): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/${configId}/steps/reorder`,
      method: 'POST',
      data: { steps },
    });
    return response.data;
  },

  patchMeta: async (
    configId: string,
    meta: { icon?: string; remark?: string; listingCategoryId?: string | null },
  ): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/${configId}/meta`,
      method: 'PATCH',
      data: meta,
    });
    return response.data;
  },

  uploadIcon: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.request<{ url: string }>({
      url: '/admin/form-configs/upload-icon',
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  reset: async (configId: string): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/${configId}/reset`,
      method: 'POST',
    });
    return response.data;
  },

  setFieldStatus: async (
    fieldId: string,
    isActive: boolean,
  ): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/fields/${fieldId}/status`,
      method: 'PATCH',
      data: { isActive },
    });
    return response.data;
  },

  deleteField: async (fieldId: string): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/fields/${fieldId}`,
      method: 'DELETE',
    });
    // API returns the updated config after deletion
    return response.data;
  },

  setStatus: async (configId: string, isActive: boolean): Promise<FormConfig> => {
    const response = await api.request<FormConfig>({
      url: `/admin/form-configs/${configId}/status`,
      method: 'PATCH',
      data: { isActive },
    });
    return response.data;
  },

  deleteConfig: async (configId: string): Promise<void> => {
    await api.request({
      url: `/admin/form-configs/${configId}`,
      method: 'DELETE',
    });
  },

  // ── Group definitions ────────────────────────────────────────────────────

  upsertGroup: async (
    configId: string,
    name: string,
    allowMultiple: boolean,
    maxInstances?: number | null,
  ): Promise<FormGroupDef> => {
    const response = await api.request<FormGroupDef>({
      url: `/admin/form-configs/${configId}/groups/${encodeURIComponent(name)}`,
      method: 'PUT',
      data: { allowMultiple, maxInstances },
    });
    return response.data;
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    await api.request({
      url: `/admin/form-configs/groups/${groupId}`,
      method: 'DELETE',
    });
  },

  // ── Section options ──────────────────────────────────────────────────────

  getSectionOptions: async (): Promise<FormConfigSectionOptions> => {
    const response = await api.request<FormConfigSectionOptions>({
      url: '/admin/form-configs/section-options',
      method: 'GET',
    });
    return response.data;
  },

  createSectionOption: async (
    sectionType: 'listing' | 'detail',
    value: string,
  ): Promise<FormConfigSectionOption> => {
    const response = await api.request<FormConfigSectionOption>({
      url: '/admin/form-configs/section-options',
      method: 'POST',
      data: { sectionType, value },
    });
    return response.data;
  },

  deleteSectionOption: async (id: string): Promise<void> => {
    await api.request({
      url: `/admin/form-configs/section-options/${id}`,
      method: 'DELETE',
    });
  },

  // ── Pricing ──────────────────────────────────────────────────────────────

  getPricing: async (): Promise<FormPricingResponse> => {
    const response = await api.request<FormPricingResponse>({
      url: '/admin/form-configs/pricing',
      method: 'GET',
    });
    return response.data;
  },

  updateFormPricing: async (id: string, dto: { listingCost?: number; visibleDays?: number }): Promise<FormPricingRow> => {
    const response = await api.request<FormPricingRow>({
      url: `/admin/form-configs/${id}/pricing`,
      method: 'PATCH',
      data: dto,
    });
    return response.data;
  },

  updateAddonConfig: async (dto: Partial<{ boostCost: number; boostDays: number; topOfPageCost: number; topOfPageDays: number; whatsappCost: number; whatsappDays: number; popupAdCost: number; popupAdDays: number }>): Promise<void> => {
    await api.request({
      url: '/admin/form-configs/addon-config',
      method: 'PATCH',
      data: dto,
    });
  },

  flushCache: async (): Promise<void> => {
    await api.request({
      url: '/admin/form-configs/flush-cache',
      method: 'POST',
    });
  },
};
